/**
 * An insight spec becomes one SQL statement over a record's `v_<record>` view.
 *
 * This module builds SQL and never runs it, exactly as `insight-views.ts`
 * builds the view DDL and never runs it. That is not tidiness: expo-sqlite
 * cannot load under bun, so a module that imports `db.ts` cannot be unit
 * tested. Everything here is pure, so `insight-query.test.ts` proves the
 * statements without a phone. `entry-repo.ts` binds this to `queryRaw`.
 *
 * Identifiers are interpolated; values are bound. SQLite has no parameter for
 * a table or a column name, so the view name and the `f_<fieldId>` columns go
 * through the same `quoteIdent` and name mangling `insight-views.ts` already
 * tests against hostile input. Every day boundary and every limit is a `?`.
 *
 * Two conditions are on every statement.
 *
 *   deleted_at IS NULL   a tombstone is not money
 *   the day window       null `day` means the entry has no date, so it can
 *                        only ever count under scope 'all'
 *
 * A spec may carry its own conditions on top — "only count entries where the
 * payment mode is Credit". They are ANDed, they are the same on every shape,
 * and they always name a field of the record's own view. Nothing here knows
 * what a payment mode is: a condition is a field id, an operator from a closed
 * list, and a bound value.
 *
 * THREE THINGS REACH PAST ONE COLUMN, and all three need the record schemas,
 * which is what `InsightCompileContext` carries.
 *
 *   a hop      `via` on a condition, `groupVia`, `leaderVia`, or a hop inside a
 *              recipe. One scalar subquery into the pointed record's view.
 *   a recipe   a row formula as the measure: worked out per row, then added up.
 *              The same closed tree a formula field holds, said in SQL.
 *   a combine  a measure whose leaves are TOTALS, with arithmetic over them.
 *              One subquery per leaf, each over its own record's view. This is
 *              the only card that reads two records at once, and it is why the
 *              Dashboard hosts a record's spec.
 *
 * A card that needs a schema this device has not pulled compiles to a statement
 * answering NO ROWS. It draws the quiet placeholder, never a number: a wrong
 * number under a confident label is the one failure worse than a missing card.
 *
 * Pending rows are counted, matching `todayTotals`: a sale saved offline is
 * still money today.
 *
 * One row per entry is assumed, and that holds: `entry_mirror.entry_id` is the
 * primary key and `applyPulled` skips a version that is not current, so a
 * superseded revision never sits beside the one that replaced it.
 */
import {
  INSIGHT_AGG_MAX_LEAVES,
  isNumericType,
  readableFieldType,
  type FormulaNode,
  type FormulaOnMissing,
  type FormulaValueType,
  type InsightAggLeaf,
  type InsightAggNode,
  type InsightFilterOp,
  type InsightRowSource,
  type InsightShape,
  type InsightWhere,
  type RecordSchemaResponse,
  formulaOf,
} from '@alluzo/shared';

import { columnNameFor, quoteIdent, viewNameFor } from '@/lib/insight-views';

export type { InsightShape };

/* ------------------------------------------------------------------- spec */

/**
 * Which number the aggregate reads.
 *
 * The shared shape, not a second one. `measure` is the materialized
 * `signed_value` column, `field` is one json_extract column, and `expr` is a
 * row formula worked out per row and then added up — the same closed tree a
 * formula field holds, so "total of litres times rate" needs no stored column.
 */
export type InsightSource = InsightRowSource;

/**
 * What to compute.
 *
 * `rows` is `COUNT(1)` and reads nothing, which is why it is the one member
 * with no source: a record with no measure can still be counted.
 *
 * `formula` is the other tree: its leaves are totals, one subquery each, and
 * its branches are arithmetic over them. "What each customer owes" is one
 * leaf minus another, and no row can be asked it — which is exactly why it is
 * a second grammar and not a wider row source.
 */
export type InsightMeasure =
  | { readonly agg: 'sum' | 'avg' | 'min' | 'max' | 'count'; readonly of: InsightSource }
  | { readonly agg: 'rows' }
  | { readonly agg: 'formula'; readonly expr: InsightAggNode };

/** The window. 'all' is the whole mirror and is the only scope a dateless entry reaches. */
export type InsightScope = 'day' | 'week' | 'month' | 'year' | 'all';

/** A scope that has a period before it, so a pair can compare against one. */
export type InsightPairScope = Exclude<InsightScope, 'all'>;

/* -------------------------------------------------------------- conditions */

/**
 * What a condition asks of one field.
 *
 *   is / is_not   the stored value, exactly. A reference holds an entry id and
 *                 a select holds an option id, so this is how "the payment
 *                 mode is Credit" is said without any word for Credit.
 *   in / not_in   "is any of" and "is none of": the same, over a list.
 *   gt/gte/lt/lte a number, compared as a number.
 *   set / unset   whether the field was answered at all.
 *
 * `is_not` (and `not_in`) is deliberately true for an entry with no value: an
 * entry with no payment mode is not on credit. Plain `<> ?` would answer NULL there and drop
 * the row, which reads as "we could not tell" and is not what anybody means.
 */
export type { InsightFilterOp };

/** The ops that ask about presence and so carry no value. */
export const PRESENCE_OPS: readonly InsightFilterOp[] = ['set', 'unset'];

/** The ops that compare as numbers rather than as stored values. */
export const NUMERIC_OPS: readonly InsightFilterOp[] = ['gt', 'gte', 'lt', 'lte'];

/** The ops that compare against a list of stored values. */
export const LIST_OPS: readonly InsightFilterOp[] = ['in', 'not_in'];

export const INSIGHT_FILTER_OPS: readonly InsightFilterOp[] = [
  'is',
  'is_not',
  'in',
  'not_in',
  'gt',
  'gte',
  'lt',
  'lte',
  'set',
  'unset',
];

/**
 * One condition on the rows a card counts.
 *
 * The shared shape. `value` is absent for `set` and `unset`, and bound as it is
 * given, so a reference or a select passes its id as a string and a number
 * passes as a number — SQLite does not compare TEXT to INTEGER, and
 * `json_extract` answers in the JSON value's own type.
 *
 * `via` is a reference field on this record, and then `fieldId` names a field
 * on the row it points at: "only the customers on credit terms". The compiler
 * draws that as one scalar subquery, and it needs the schema to do it.
 */
export type InsightFilter = InsightWhere;

/** Every spec may carry conditions. They are ANDed, and they are optional. */
type Filtered = {
  readonly filters?: readonly InsightFilter[];
};

/**
 * One number. With `leaderOf` it is the largest group's number and the result
 * carries that group's key, which is how "Top item" is served without a card
 * of its own.
 */
export type ScalarSpec = Filtered & {
  readonly shape: 'scalar';
  readonly measure: InsightMeasure;
  readonly scope: InsightScope;
  readonly leaderOf?: string;
  /**
   * A reference on this record, so the ranking is by a field on the row it
   * points at: "the biggest area", not "the biggest customer". `leaderOf` is
   * then a field on the record `leaderVia` targets.
   */
  readonly leaderVia?: string;
};

/** The same number twice: this period, and the one before it. */
export type PairSpec = Filtered & {
  readonly shape: 'scalar_pair';
  readonly measure: InsightMeasure;
  readonly scope: InsightPairScope;
};

/** One number per day, for the last `days` days, today included. */
export type SeriesSpec = Filtered & {
  readonly shape: 'series';
  readonly measure: InsightMeasure;
  readonly days: number;
};

/** One number per group, ranked. `limit` is how many the card draws. */
export type BreakdownSpec = Filtered & {
  readonly shape: 'breakdown';
  readonly measure: InsightMeasure;
  readonly groupFieldId: string;
  /**
   * A reference on this record, so the keys are a field on the row it points
   * at: deliveries by the customer's AREA rather than by the customer.
   * `groupFieldId` is then a field on the record `groupVia` targets.
   */
  readonly groupVia?: string;
  readonly scope: InsightScope;
  readonly limit: number;
};

export type InsightSpec = ScalarSpec | PairSpec | SeriesSpec | BreakdownSpec;

/* ------------------------------------------------------- the schema it needs */

/**
 * What the compiler needs beyond the spec, and only sometimes.
 *
 * A plain card names columns of one view and needs nothing. A hop has to know
 * which record a reference points at and what type the field on the other side
 * is, and only the cached schema knows either. `entry-repo.ts` answers it from
 * `schema_cache`, synchronously, because a card is compiled during a render.
 *
 * A card that needs this and cannot have it compiles to a statement answering
 * NO ROWS. It draws the same quiet placeholder as a record whose view has not
 * been built — which is the honest failure. A wrong number under a confident
 * label is the one failure worse than a missing card.
 */
export type InsightCompileContext = {
  readonly schemaOf: (recordId: string) => RecordSchemaResponse | null;
};

/** The context a row formula compiles against: one record's view, plus the schemas. */
export type RowExprContext = InsightCompileContext & {
  /** Whose view the unqualified columns belong to. A hop reads out of it. */
  readonly recordId: string;
  /** The formula's own forgiveness rule. `zero` reads a missing number as 0. */
  readonly onMissing?: FormulaOnMissing;
};

/**
 * A card that cannot be compiled, for a reason that is not a defect.
 *
 * A schema this device has not pulled, a reference that no longer points
 * anywhere, a combined card with more totals than the cap allows. `compileInsight`
 * catches it and answers the no-rows statement; anything else still throws,
 * because anything else is a bug in this file.
 */
export class InsightCompileError extends Error {}

/* ------------------------------------------------------------ runtime cut */

/**
 * The filter a person has on the entry list, applied to a card as it is drawn.
 *
 * **Never stored, and that is the whole reason it can exist.** A card's own
 * conditions are `InsightFilter`s written into `insight_layout`, and
 * `insight-layout.ts` refuses any shape it cannot compile — one bad condition
 * drops the WHOLE card, so a newer build writing a richer condition would make
 * cards vanish on an older phone. A cut is handed in at the moment of drawing
 * and parsed back by nobody, so it may say things a stored condition may not.
 * `entry-filter.ts` builds it.
 *
 * The window REPLACES the spec's own period rather than narrowing it. A card
 * called "Today" intersected with a last-month filter would draw a zero under a
 * name that promised otherwise, and a wrong number under a confident label is
 * the one failure worse than a missing card.
 */
export type RuntimeCut = {
  /** Replaces the spec's own period. Null keeps the scope, or the series days. */
  readonly window: DayWindow | null;
  /** ANDed onto every WHERE. Already compiled against the record's view columns. */
  readonly predicate: { readonly sql: string; readonly params: readonly (string | number)[] } | null;
};

/**
 * Conditions in one order, with repeats dropped.
 *
 * A card's id is the hash of what it draws, and two conditions asked in a
 * different order draw the same thing. Without this, adding them in the other
 * order would make a second card asking the identical question.
 */
export const sortFilters = (
  filters: readonly InsightFilter[] | undefined,
): readonly InsightFilter[] | undefined => {
  if (filters === undefined || filters.length === 0) return undefined;
  const seen = new Set<string>();
  const kept: InsightFilter[] = [];
  for (const given of filters) {
    // "Is any of A, B" and "is any of B, A" ask one thing, so a list is put in
    // one order before it is keyed, and a repeat inside it is dropped.
    const filter = sortedValues(given);
    // The hop is part of what a condition asks: the customer's terms and this
    // row's terms are two questions, not one asked twice.
    const key = `${filter.fieldId}|${filter.op}|${valueKey(filter)}|${filter.via ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    kept.push(filter);
  }
  kept.sort((a, b) => {
    if (a.fieldId !== b.fieldId) return a.fieldId < b.fieldId ? -1 : 1;
    if (a.op !== b.op) return a.op < b.op ? -1 : 1;
    const left = valueKey(a);
    const right = valueKey(b);
    if (left !== right) return left < right ? -1 : 1;
    const here = a.via ?? '';
    const there = b.via ?? '';
    return here < there ? -1 : here > there ? 1 : 0;
  });
  return kept;
};

/**
 * A condition's value as one string, for keying and ordering. A list joins its
 * members with a character no id or option id holds, so "A,B" as one value and
 * A and B as two never meet.
 */
export const valueKey = (filter: InsightFilter): string =>
  Array.isArray(filter.value) ? filter.value.map(String).join('\u001f') : String(filter.value ?? '');

/** A list condition with its values in one order and no repeats; any other unchanged. */
const sortedValues = (filter: InsightFilter): InsightFilter => {
  if (!Array.isArray(filter.value)) return filter;
  const seen = new Set<string>();
  const values: (string | number)[] = [];
  for (const one of filter.value as readonly (string | number)[]) {
    const key = `${typeof one}:${String(one)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    values.push(one);
  }
  values.sort((a, b) => {
    const left = `${typeof a}:${String(a)}`;
    const right = `${typeof b}:${String(b)}`;
    return left < right ? -1 : left > right ? 1 : 0;
  });
  return { ...filter, value: values };
};

/* ----------------------------------------------------------------- results */

export type ScalarResult = {
  readonly shape: 'scalar';
  readonly value: number | null;
  /** The winning group's stored key when the spec named `leaderOf`, else null. */
  readonly key: string | null;
};

export type PairResult = {
  readonly shape: 'scalar_pair';
  readonly value: number | null;
  readonly against: number | null;
};

export type SeriesPoint = { readonly day: string; readonly value: number };

export type SeriesResult = { readonly shape: 'series'; readonly points: readonly SeriesPoint[] };

export type BreakdownSlice = { readonly key: string; readonly value: number };

export type BreakdownResult = {
  readonly shape: 'breakdown';
  readonly slices: readonly BreakdownSlice[];
  /** Everything past `limit`, netted. Null when nothing was left over. */
  readonly other: { readonly count: number; readonly value: number } | null;
  /**
   * Set when the groups overlap: grouped by a multi select, an entry sits in
   * every group it holds, so the slices add up to more than the entries.
   * `total` is then the measure over each entry once, under the same scope and
   * conditions (Decision 8 of `dependent-choices-and-multi-select.md`).
   */
  readonly overlap?: { readonly total: number | null };
};

export type InsightResult = ScalarResult | PairResult | SeriesResult | BreakdownResult;

/**
 * The most groups one breakdown statement will scan. A card draws a handful
 * and nets the rest, so this only has to be past any real shop's month. It is
 * a bound on the work, not on the answer.
 */
export const GROUP_SCAN_LIMIT = 500;

/** What `compileInsight` answers: a statement, and the values to bind to it. */
export type CompiledInsight = {
  readonly sql: string;
  readonly params: readonly (string | number)[];
};

/* -------------------------------------------------------------- day windows */

const DAY_MS = 86_400_000;

const dayToUtc = (day: string): number =>
  Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));

const utcToDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

const addDays = (day: string, count: number): string => utcToDay(dayToUtc(day) + count * DAY_MS);

/** Monday, per ISO 8601. getUTCDay is 0 on Sunday, so Sunday walks back six. */
const weekStart = (day: string): string => {
  const weekday = new Date(dayToUtc(day)).getUTCDay();
  return addDays(day, -((weekday + 6) % 7));
};

/** Day 0 of the next month is the last day of this one. */
const monthEnd = (year: number, monthIndex: number): string => utcToDay(Date.UTC(year, monthIndex + 1, 0));

export type DayWindow = { readonly from: string; readonly to: string };

/**
 * The window one scope covers, ending at the period's own end rather than at
 * today. "This month" means the month, and an entry cannot be dated past it
 * unless somebody dated it forward on purpose — in which case it belongs to
 * the month it says.
 */
export const windowFor = (scope: InsightPairScope, today: string): DayWindow => {
  const year = Number(today.slice(0, 4));
  const monthIndex = Number(today.slice(5, 7)) - 1;
  switch (scope) {
    case 'day':
      return { from: today, to: today };
    case 'week': {
      const from = weekStart(today);
      return { from, to: addDays(from, 6) };
    }
    case 'month':
      return { from: `${today.slice(0, 7)}-01`, to: monthEnd(year, monthIndex) };
    case 'year':
      return { from: `${today.slice(0, 4)}-01-01`, to: `${today.slice(0, 4)}-12-31` };
  }
};

/** The window immediately before the one `windowFor` answers. */
export const previousWindowFor = (scope: InsightPairScope, today: string): DayWindow => {
  const current = windowFor(scope, today);
  switch (scope) {
    case 'day':
      return { from: addDays(current.from, -1), to: addDays(current.from, -1) };
    case 'week':
      return { from: addDays(current.from, -7), to: addDays(current.from, -1) };
    case 'month': {
      const year = Number(current.from.slice(0, 4));
      const monthIndex = Number(current.from.slice(5, 7)) - 1;
      const from = utcToDay(Date.UTC(year, monthIndex - 1, 1));
      return { from, to: monthEnd(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1) };
    }
    case 'year': {
      const year = Number(current.from.slice(0, 4)) - 1;
      return { from: `${year}-01-01`, to: `${year}-12-31` };
    }
  }
};

/** The window a series covers: `days` days back from today, today included. */
export const seriesWindow = (days: number, today: string): DayWindow => ({
  from: addDays(today, -(Math.max(1, Math.trunc(days)) - 1)),
  to: today,
});

/* ------------------------------------------------------------- expressions */

/** The schema of one record, or a refusal. Never a guess. */
const schemaOrRefuse = (ctx: RowExprContext, recordId: string): RecordSchemaResponse => {
  const schema = ctx.schemaOf(recordId);
  if (schema === null) throw new InsightCompileError(`no cached schema for ${recordId}`);
  return schema;
};

/**
 * What one field of a record reads as, inside a formula.
 *
 * A formula field declares its own result, and since migration 0011 its answer
 * sits in `values_json` like any typed value — so a formula over a formula is
 * a column read like any other.
 */
const valueTypeOf = (schema: RecordSchemaResponse, fieldId: string): FormulaValueType | null => {
  const field = schema.fields.find((candidate) => candidate.id === fieldId);
  if (field === undefined) return null;
  if (field.type === 'formula') return formulaOf(field.spec)?.result ?? null;
  return readableFieldType(field.type);
};

/**
 * One column, read as the thing it holds.
 *
 * `NULLIF` on every read, whatever the type, because `isFilled` in the shared
 * evaluator counts the empty string as nothing — and a field cleared to nothing
 * stores exactly that. Without it, `if Litres is filled` and "Litres or else the
 * usual" would answer one thing on the entry form and another on the card, and
 * the whole promise of Section 5.7 rule 6 is that they answer the same.
 *
 * A number is cast on top, because `json_extract` answers a JSON string as TEXT
 * and '9' > '10' as text. `CAST('' AS REAL)` is 0, which is why the NULLIF has
 * to come first: a zero nobody typed would then be added up.
 *
 * Text, a date and a boolean take no cast: an equality, a `julianday` and a
 * `CASE` each want the stored value.
 */
const readColumnSql = (
  schema: RecordSchemaResponse,
  fieldId: string,
  column: string,
): string => {
  const type = valueTypeOf(schema, fieldId);
  if (type === null) throw new InsightCompileError(`no readable field ${fieldId}`);
  const filled = `NULLIF(${column}, '')`;
  return isNumericType(type) ? `CAST(${filled} AS REAL)` : filled;
};

/**
 * One hop, as a scalar subquery into the pointed record's own view.
 *
 * The host column is written with its view's name in front of it, so the
 * correlation survives a record that points at ITSELF — unqualified, `f_via`
 * would bind to the subquery's own row and the hop would read the row it
 * started from.
 *
 * It binds nothing: every part of it is an identifier. That is what lets a
 * group key repeat in the SELECT, the WHERE and the GROUP BY without a single
 * value being bound three times for one push.
 */
const hopSql = (
  via: string,
  fieldId: string,
  ctx: RowExprContext,
  typed: boolean,
): string => {
  const host = schemaOrRefuse(ctx, ctx.recordId);
  const reference = host.fields.find((candidate) => candidate.id === via) ?? null;
  const targetId = reference?.targetRecordId ?? null;
  if (targetId === null) throw new InsightCompileError(`${via} points at no record`);
  const target = schemaOrRefuse(ctx, targetId);
  if (valueTypeOf(target, fieldId) === null) {
    // The field is not on the pointed record, so the view has no column for it
    // and the statement would fail on the phone. Refusing here draws nothing.
    throw new InsightCompileError(`no readable field ${fieldId} on ${targetId}`);
  }
  const column = `t.${quoteIdent(columnNameFor(fieldId))}`;
  const inner = typed ? readColumnSql(target, fieldId, column) : column;
  return (
    `(SELECT ${inner} FROM ${quoteIdent(viewNameFor(targetId))} t ` +
    `WHERE t.entry_id = ${quoteIdent(viewNameFor(ctx.recordId))}.${quoteIdent(columnNameFor(via))} ` +
    `AND t.deleted_at IS NULL LIMIT 1)`
  );
};

const CALC_SIGNS: Readonly<Record<'add' | 'subtract' | 'multiply' | 'divide', string>> = {
  add: '+',
  subtract: '-',
  multiply: '*',
  divide: '/',
};

const TEST_SIGNS: Readonly<Record<'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte', string>> = {
  eq: '=',
  ne: '<>',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
};

/**
 * A row formula, as one SQL expression over the record's own view.
 *
 * The same closed tree `evaluateFormula` runs, said in SQL instead of in
 * JavaScript — one node kind at a time, with no parser on either side. A field
 * is a column, a hop is a scalar subquery, everything else is an operator
 * SQLite already has.
 *
 * Two rules make it safe. Every identifier goes through `quoteIdent` and the
 * name mangling `insight-views.ts` tests against hostile input; every value is
 * pushed onto `params` at the moment its `?` is written, so the marks and the
 * pushes cannot drift apart. That is also why a subtree that appears twice in
 * the text is COMPILED twice: reusing the string would put two marks in the
 * statement for one bound value, and SQLite would bind the wrong thing into
 * the middle of a CASE without complaining.
 *
 * A divide by zero answers nothing, not infinity and not an error, which is
 * the rule the evaluator keeps.
 */
export const compileRowExpr = (
  node: FormulaNode,
  ctx: RowExprContext,
  params: (string | number)[],
): string => {
  const part = (kid: FormulaNode): string => compileRowExpr(kid, ctx, params);
  /**
   * One operand of the arithmetic, with the forgiveness rule applied.
   *
   * `zero` belongs HERE and not on the leaves, exactly as `numeric()` applies
   * it in the shared evaluator. On the leaves it would kill every "or else":
   * zero is a filled value, so `coalesce(litres, the usual)` would take the
   * litres every time and the fallback would be dead code.
   */
  const operand = (kid: FormulaNode): string =>
    ctx.onMissing === 'zero' ? `COALESCE(${part(kid)}, 0)` : part(kid);

  switch (node.kind) {
    case 'field': {
      const schema = schemaOrRefuse(ctx, ctx.recordId);
      return readColumnSql(schema, node.fieldId, quoteIdent(columnNameFor(node.fieldId)));
    }

    case 'hop':
      return hopSql(node.via, node.fieldId, ctx, true);

    case 'const':
      // A boolean is stored as JSON true, which json_extract answers as 1.
      params.push(typeof node.value === 'boolean' ? (node.value ? 1 : 0) : node.value);
      return '?';

    case 'calc': {
      if (node.op === 'divide') {
        // The right side is named three times, so it is compiled three times.
        // A division by zero has no forgiving reading: zero is an answer the
        // person gave, not one they left out, so it answers nothing.
        const zero = operand(node.right);
        const missing = operand(node.right);
        const left = operand(node.left);
        const right = operand(node.right);
        return `CASE WHEN (${zero}) = 0 OR (${missing}) IS NULL THEN NULL ELSE (${left}) / (${right}) END`;
      }
      const left = operand(node.left);
      const right = operand(node.right);
      return `(${left} ${CALC_SIGNS[node.op]} ${right})`;
    }

    case 'round':
      // The places are a validated small integer from the tree, never text.
      return `ROUND(${operand(node.of)}, ${Math.trunc(node.decimals)})`;

    case 'if': {
      const left = part(node.left);
      const test =
        node.op === 'filled'
          ? `${left} IS NOT NULL`
          : node.op === 'empty'
            ? `${left} IS NULL`
            : `${left} ${TEST_SIGNS[node.op]} ${node.right === undefined ? 'NULL' : part(node.right)}`;
      return `CASE WHEN ${test} THEN ${part(node.then)} ELSE ${part(node.otherwise)} END`;
    }

    case 'days_between':
      // `substr` first: a datetime is stored with its time on it, and julianday
      // of a day boundary is what "days between" means.
      return (
        `CAST(julianday(substr(${part(node.to)}, 1, 10)) - ` +
        `julianday(substr(${part(node.from)}, 1, 10)) AS INTEGER)`
      );

    case 'concat':
      // A missing part joins as nothing. `||` with a NULL answers NULL, which
      // would lose the whole sentence for one unfilled field.
      return `(${node.parts.map((kid) => `COALESCE(${part(kid)}, '')`).join(' || ')})`;

    case 'coalesce':
      return `COALESCE(${node.of.map(part).join(', ')})`;

    case 'agg':
      // A total over chosen rows, inside a measure that is itself totalled:
      // `readRowSource` refuses it, and this is the second door.
      throw new InsightCompileError('a total over chosen rows inside a measure');

    default:
      // A tree from a newer build. Nothing here can draw it, and guessing is
      // how a card shows a number that means something else.
      throw new InsightCompileError('a recipe this build cannot read');
  }
};

/** The aggregate function one measure names. `rows` and `count` are both COUNT. */
const aggFn = (agg: string): string => (agg === 'rows' ? 'COUNT' : agg.toUpperCase());

/**
 * The expression an aggregate is applied to, pushing whatever it binds.
 *
 * Called at the point in the statement where the expression is written, never
 * before it: a recipe measure binds values, and they have to land in the order
 * the `?` marks appear.
 */
const innerOf = (
  agg: string,
  of: InsightSource | undefined,
  ctx: RowExprContext | null,
  params: (string | number)[],
): string => {
  if (agg === 'rows') return '1';
  if (of === undefined) throw new InsightCompileError('a total of nothing');
  if (of.kind === 'expr') {
    if (ctx === null) throw new InsightCompileError('a recipe measure needs the schemas');
    return compileRowExpr(of.expr, { ...ctx, onMissing: of.onMissing }, params);
  }
  const column = of.kind === 'measure' ? 'signed_value' : quoteIdent(columnNameFor(of.fieldId));
  // COUNT wants the raw column: it counts what is there, and a cast of null is
  // still null. Every other aggregate wants a number, and a value that arrived
  // as a JSON string would otherwise sum to zero.
  return agg === 'count' ? column : `CAST(${column} AS REAL)`;
};

/** The aggregate function, and the expression it is applied to. */
const aggregateOf = (
  measure: InsightMeasure,
  ctx: RowExprContext | null,
  params: (string | number)[],
): { fn: string; inner: string } => {
  if (measure.agg === 'formula') throw new InsightCompileError('a combined card compiles on its own');
  const of = measure.agg === 'rows' ? undefined : measure.of;
  return { fn: aggFn(measure.agg), inner: innerOf(measure.agg, of, ctx, params) };
};

/**
 * How a ranking orders itself: by SIZE, never by signed value.
 *
 * A "top" list is about how big something is, and the sign already says which
 * direction it points. Ordering by the signed value gets a money-in record
 * right by accident and a money-out record exactly backwards: on Shop
 * Expenses, where `signed_value` is negative on every row, "Top category"
 * ranked the -300 of tea above the -50000 of rent and named tea the top
 * category. Ranking by magnitude is right in both directions and needs to know
 * nothing about the business.
 *
 * On a record whose values are all positive this changes nothing, so no card
 * on a sales record moves.
 */
const RANK = 'ABS("value") DESC';

const from = (recordId: string): string => `FROM ${quoteIdent(viewNameFor(recordId))}`;

/** The alias of a multi select's members, and the key a group by them reads. */
const MEMBER = '"m"';
const MEMBER_KEY = `${MEMBER}."value"`;

/**
 * A grouped statement, ranked. Over a multi select's members it is wrapped
 * first: `json_each` has a column of its own called "value", and SQLite reads
 * a name in an ORDER BY expression off the FROM before the result's aliases,
 * so `ABS("value")` would rank by the member's id (ABS of "81071c8a…" is
 * 81071) and put Colour in 2 visits above Cut in 3. Outside the wrapper the
 * only "value" is the group's number.
 */
const ranked = (grouped: string, overMembers: boolean, limit: '1' | '?'): string =>
  overMembers
    ? `SELECT "key", "value" FROM (${grouped}) ORDER BY ${RANK} LIMIT ${limit}`
    : `${grouped} ORDER BY ${RANK} LIMIT ${limit}`;

/** Whether a field of this record holds an array: a multi select. */
const isMultiSelect = (ctx: InsightCompileContext, recordId: string, fieldId: string): boolean =>
  ctx.schemaOf(recordId)?.fields.find((field) => field.id === fieldId)?.type === 'multi_select';

/**
 * The one number an overlapping breakdown's headline reads: the same measure,
 * scope and conditions, with each entry counted once.
 */
export const overlapTotalSpec = (spec: BreakdownSpec): ScalarSpec => ({
  shape: 'scalar',
  measure: spec.measure,
  scope: spec.scope,
  ...(spec.filters === undefined ? {} : { filters: spec.filters }),
});

/**
 * Whether a breakdown groups by a multi select, so an entry can sit in more
 * than one of its groups and the groups do not add up to the total.
 */
export const groupsOverlap = (spec: InsightSpec, recordId: string, ctx: InsightCompileContext): boolean =>
  spec.shape === 'breakdown' && spec.groupVia === undefined && isMultiSelect(ctx, recordId, spec.groupFieldId);

/**
 * One condition, as a predicate, binding its value.
 *
 * The value is pushed only when a predicate is really produced, so `params`
 * stays in the order the `?` marks appear. A comparison with no value is not a
 * condition at all and is dropped rather than guessed at.
 *
 * A numeric comparison casts, for the same reason `aggregateOf` casts: a value
 * that arrived as a JSON string would otherwise compare as text, where '9' is
 * greater than '10'.
 */
const SIGNS: Readonly<Record<'gt' | 'gte' | 'lt' | 'lte', string>> = {
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
};

const filterSql = (
  filter: InsightFilter,
  params: (string | number)[],
  ctx: RowExprContext | null,
): string | null => {
  // A hop compares the row the reference points at, in place of the column.
  // The subquery binds nothing, so the rest of this function is unchanged.
  if (filter.via !== undefined && ctx === null) {
    throw new InsightCompileError('a condition through a reference needs the schemas');
  }
  const column =
    filter.via === undefined || ctx === null
      ? quoteIdent(columnNameFor(filter.fieldId))
      : hopSql(filter.via, filter.fieldId, ctx, false);
  // An unanswered field is absent from values_json, so json_extract answers
  // NULL. A field cleared to nothing can answer the empty string. Both are
  // "not answered", and both must land on the same side.
  if (filter.op === 'set') return `(${column} IS NOT NULL AND ${column} <> '')`;
  if (filter.op === 'unset') return `(${column} IS NULL OR ${column} = '')`;
  if (filter.value === undefined) return null;
  if (filter.op === 'in' || filter.op === 'not_in') {
    // A list on a list op, and nowhere else: a shape the reader refuses is a
    // card this device cannot answer, never a condition quietly dropped.
    if (!Array.isArray(filter.value) || filter.value.length === 0) {
      throw new InsightCompileError('"is any of" needs a list of values');
    }
    const values = filter.value as readonly (string | number)[];
    params.push(...values);
    const marks = values.map(() => '?').join(', ');
    return filter.op === 'in'
      ? `${column} IN (${marks})`
      : `(${column} IS NULL OR ${column} NOT IN (${marks}))`;
  }
  if (Array.isArray(filter.value)) {
    throw new InsightCompileError('a list of values needs "is any of" or "is none of"');
  }
  const value = filter.value as string | number;
  if (filter.op === 'is') {
    params.push(value);
    return `${column} = ?`;
  }
  if (filter.op === 'is_not') {
    params.push(value);
    return `(${column} IS NULL OR ${column} <> ?)`;
  }
  params.push(value);
  return `CAST(${column} AS REAL) ${SIGNS[filter.op as keyof typeof SIGNS]} ?`;
};

/** Every condition of a spec, ANDed onto a WHERE that already has one term. */
const filtersSql = (
  filters: readonly InsightFilter[] | undefined,
  params: (string | number)[],
  ctx: RowExprContext | null,
): string => {
  if (filters === undefined || filters.length === 0) return '';
  const parts: string[] = [];
  for (const filter of filters) {
    const predicate = filterSql(filter, params, ctx);
    if (predicate !== null) parts.push(predicate);
  }
  return parts.length === 0 ? '' : ` AND ${parts.join(' AND ')}`;
};

/* ---------------------------------------------------------------- compiler */

/**
 * One spec, one statement. The `params` array is in the order the `?` marks
 * appear, so the caller passes it straight to `getAllSync`.
 *
 * @param today the workspace's day, `YYYY-MM-DD` (`workspaceToday`): the day a
 *              `date` field holds, which is what `dayOf` materializes
 *              `entry_mirror.day` with (R2 of docs/plans/qa-fixes-2.md).
 */
export const compileInsight = (
  spec: InsightSpec,
  recordId: string,
  today: string,
  cut?: RuntimeCut,
  ctx?: InsightCompileContext,
): CompiledInsight => {
  try {
    const rows: RowExprContext | null =
      ctx === undefined ? null : { schemaOf: ctx.schemaOf, recordId };
    return spec.measure.agg === 'formula'
      ? combinedInsight(spec, spec.measure.expr, recordId, today, cut, rows)
      : plainInsight(spec, recordId, today, cut, rows);
  } catch (error) {
    // A card this device cannot answer draws nothing. Every other fault is a
    // defect in this file, and its tests cover those.
    if (error instanceof InsightCompileError) return { sql: NO_ROWS[spec.shape], params: [] };
    throw error;
  }
};

/* ------------------------------------------------------------- the horizon */

/**
 * The oldest day this record's mirror still holds, or null when it holds
 * everything.
 *
 * `record_catalog.floor_day`, as the prune left it. Null is the ordinary case
 * and means the statements below are exactly what they always were.
 */
export type InsightHorizon = { readonly floorDay: string | null };

/**
 * The earliest day a card asks about, or null when it asks about all of them.
 *
 * One answer per shape, and it must match what the shape's own statement does,
 * or the guard would send a card to the buckets that the mirror could have
 * answered, or worse, the other way about.
 */
const earliestDayOf = (spec: InsightSpec, today: string, cut: RuntimeCut | undefined): string | null => {
  if (cut?.window != null) return cut.window.from;
  switch (spec.shape) {
    case 'series':
      return seriesWindow(spec.days, today).from;
    case 'scalar_pair':
      // A pair reaches back through the period BEFORE its own.
      return previousWindowFor(spec.scope, today).from;
    case 'scalar':
    case 'breakdown':
      return spec.scope === 'all' ? null : windowFor(spec.scope, today).from;
  }
};

/**
 * Can this card be answered from the buckets at all?
 *
 * A bucket holds one number per record, per day: the record's own measure,
 * summed, and the count of rows beside it. It has no group dimension, no filter
 * dimension and no second record. So the answer is yes for exactly two
 * questions — "how much" and "how many" — asked of one record with nothing
 * else attached, and no for everything else.
 *
 * The refusals are not a shortcut. A breakdown summed out of a dimension that
 * is not there, or a filtered total taken from an unfiltered sum, is a wrong
 * number under a confident label, which is the one failure this whole design
 * exists to prevent. A card that cannot be answered draws the quiet
 * placeholder instead.
 */
const bucketMeasure = (spec: InsightSpec): 'value' | 'rows' | null => {
  if (spec.filters !== undefined && spec.filters.length > 0) return null;
  if (spec.shape === 'breakdown') return null;
  if (spec.shape === 'scalar' && spec.leaderOf !== undefined) return null;
  if (spec.measure.agg === 'rows') return 'rows';
  // `count` counts the rows where the measure is PRESENT, which is not the row
  // count the bucket carries. `avg` needs a count per day it does not carry,
  // and neither a smallest nor a largest can be recovered from daily sums.
  if (spec.measure.agg !== 'sum') return null;
  return spec.measure.of.kind === 'measure' ? 'value' : null;
};

/**
 * One window's total, out of the buckets and the rows the server has not seen.
 *
 * Two halves, and the second is the one that is easy to forget. A bucket holds
 * only what the server has accepted, so an entry saved offline is in the mirror
 * and in no bucket. The mirror's pending rows for the same window are therefore
 * added back on, and a row that syncs simply moves from one half to the other.
 *
 * `window` null is scope `all`: every bucket, and every pending row.
 */
/**
 * Rows the server has NEVER seen, which is what the buckets are missing.
 *
 * Not every pending row. A row saved offline and never pushed is in the mirror
 * and in no bucket, so it is added. A row that was pushed and then EDITED
 * offline is in both — its old value in the bucket, its new value here — and
 * adding it would count it twice. `server_seq IS NULL` is the difference: only
 * a row the server has never accepted has no bucket behind it.
 *
 * A pending edit therefore leaves the card reading the amount the server last
 * accepted, until the push lands. That is the same lag the record's headline
 * has always had, and it is bounded by one push.
 */
const UNSEEN = 'record_id = ? AND pending = 1 AND server_seq IS NULL AND deleted_at IS NULL';

const bucketTotal = (
  recordId: string,
  column: 'value' | 'rows',
  window: DayWindow | null,
  params: (string | number)[],
): string => {
  const days = window === null ? '' : ' AND day >= ? AND day <= ?';
  params.push(recordId);
  if (window !== null) params.push(window.from, window.to);
  const banked = `SELECT COALESCE(SUM(${column}), 0) FROM aggregate_bucket WHERE record_id = ?${days}`;
  const counted = column === 'rows' ? 'COUNT(1)' : 'COALESCE(SUM(CAST(signed_value AS REAL)), 0)';
  // `entry_mirror` and not the record's view: the view carries the field
  // columns a card reads and not `server_seq`, which is the one column that
  // tells a row the server has never seen from one it has.
  params.push(recordId);
  if (window !== null) params.push(window.from, window.to);
  const pending = `SELECT ${counted} FROM entry_mirror WHERE ${UNSEEN}${days}`;
  return `(${banked}) + (${pending})`;
};

/**
 * The guard that turns "this record has no buckets" into no answer at all.
 *
 * A sum over an empty table is nought, and nought under a confident label is
 * the failure this whole design exists to prevent. Every bucket statement is
 * therefore conditional on there being a bucket to read: a record the server
 * withheld them for, or one whose first download has not finished, draws the
 * quiet placeholder rather than a zero.
 */
const HAS_BUCKETS = 'EXISTS (SELECT 1 FROM aggregate_bucket WHERE record_id = ?)';

/**
 * A card whose window reaches past what this device still holds.
 *
 * The mirror cannot answer it — the rows are not here — so the buckets do, or
 * nothing does. Throws `InsightCompileError` for every question a bucket cannot
 * answer, which lands on the same quiet placeholder a missing schema does.
 *
 * A series comes back keyed by day, which is the shape `SeriesPoint` already
 * has, so nothing above this has to learn a second one. That is the whole
 * reason the bucket is a day and not a month.
 */
const bucketInsight = (
  spec: InsightSpec,
  recordId: string,
  today: string,
  cut: RuntimeCut | undefined,
): CompiledInsight => {
  if (cut?.predicate != null) {
    throw new InsightCompileError('a filtered card has no bucket to read');
  }
  const measure = bucketMeasure(spec);
  if (measure === null) throw new InsightCompileError('no bucket answers this card');
  const params: (string | number)[] = [];

  switch (spec.shape) {
    case 'scalar': {
      const window = cut?.window ?? (spec.scope === 'all' ? null : windowFor(spec.scope, today));
      const total = bucketTotal(recordId, measure, window, params);
      params.push(recordId);
      return { sql: `SELECT ${total} AS "value" WHERE ${HAS_BUCKETS}`, params };
    }
    case 'scalar_pair': {
      const current = windowFor(spec.scope, today);
      const previous = previousWindowFor(spec.scope, today);
      const value = bucketTotal(recordId, measure, current, params);
      const against = bucketTotal(recordId, measure, previous, params);
      params.push(recordId);
      return { sql: `SELECT ${value} AS "value", ${against} AS "against" WHERE ${HAS_BUCKETS}`, params };
    }
    case 'series': {
      const window = cut?.window ?? seriesWindow(spec.days, today);
      params.push(recordId, window.from, window.to, recordId, window.from, window.to, recordId);
      return {
        sql:
          `SELECT day AS "day", SUM(value) AS "value" FROM (` +
          `SELECT day, ${measure === 'rows' ? 'rows' : 'value'} AS value FROM aggregate_bucket ` +
          `WHERE record_id = ? AND day >= ? AND day <= ? ` +
          `UNION ALL ` +
          `SELECT day, ${measure === 'rows' ? '1' : 'CAST(signed_value AS REAL)'} AS value ` +
          `FROM entry_mirror WHERE ${UNSEEN} AND day >= ? AND day <= ?` +
          // The same guard the scalars take, on the outer select so it kills
          // every row: a record with no buckets draws nothing rather than a
          // flat line of zeroes.
          `) WHERE ${HAS_BUCKETS} GROUP BY day ORDER BY day ASC`,
        params,
      };
    }
    case 'breakdown':
      throw new InsightCompileError('a bucket carries no group');
  }
};

/**
 * `compileInsight`, with the pruned mirror taken into account.
 *
 * Three answers, and the middle one is the whole point.
 *
 *   the window starts on or after the floor   the mirror holds every row it
 *                                             asks about, so nothing changes
 *   the window reaches below the floor        the buckets answer, or nothing
 *   there is no floor                         nothing changes
 *
 * It is a SECOND function rather than a wider `compileInsight` for one reason:
 * the questions the filter sheet asks — which values are in use, what the
 * largest amount is — are questions about the rows this device holds, not
 * claims about history, and they must keep reading the mirror however much of
 * it is here. They call `compileInsight` and are right to.
 */
export const compileInsightAtHorizon = (
  spec: InsightSpec,
  recordId: string,
  today: string,
  horizon: InsightHorizon,
  cut?: RuntimeCut,
  ctx?: InsightCompileContext,
): CompiledInsight => {
  const floor = horizon.floorDay;
  if (floor === null) return compileInsight(spec, recordId, today, cut, ctx);
  const earliest = earliestDayOf(spec, today, cut);
  if (earliest !== null && earliest >= floor) return compileInsight(spec, recordId, today, cut, ctx);
  try {
    return bucketInsight(spec, recordId, today, cut);
  } catch (error) {
    if (error instanceof InsightCompileError) return { sql: NO_ROWS[spec.shape], params: [] };
    throw error;
  }
};

/**
 * The statement a card compiles to when it cannot be compiled: no rows, of the
 * right shape, so every reader above answers "nothing yet" rather than throwing.
 */
const NO_ROWS: Readonly<Record<InsightShape, string>> = {
  scalar: 'SELECT NULL AS "value" WHERE 0',
  scalar_pair: 'SELECT NULL AS "value", NULL AS "against" WHERE 0',
  series: 'SELECT NULL AS "day", NULL AS "value" WHERE 0',
  breakdown: 'SELECT NULL AS "key", NULL AS "value" WHERE 0',
};

/** One spec over one record's view: the four shapes, unchanged since they shipped. */
const plainInsight = (
  spec: InsightSpec,
  recordId: string,
  today: string,
  cut: RuntimeCut | undefined,
  rows: RowExprContext | null,
): CompiledInsight => {
  const params: (string | number)[] = [];

  /**
   * The spec's own conditions, then the session filter's. Both are ANDed, and
   * the pushes happen where the `?` marks appear.
   */
  const conditions = (): string => {
    const own = filtersSql(spec.filters, params, rows);
    const extra = cut?.predicate ?? null;
    if (extra === null) return own;
    params.push(...extra.params);
    return `${own} AND (${extra.sql})`;
  };

  /**
   * `deleted_at IS NULL`, the days, and every condition. Called once per
   * statement, and the pushes happen in the order the `?` marks appear.
   *
   * A cut's window replaces the scope outright. Narrowing instead would let a
   * card keep a name that describes days the number no longer covers.
   */
  const where = (scope: InsightScope): string => {
    const window = cut?.window ?? (scope === 'all' ? null : windowFor(scope, today));
    if (window === null) return `WHERE deleted_at IS NULL${conditions()}`;
    params.push(window.from, window.to);
    return `WHERE deleted_at IS NULL AND day >= ? AND day <= ?${conditions()}`;
  };

  /**
   * The key a ranking groups on: a column of this record, or the row a
   * reference points at. The hop binds nothing, which is what lets the key be
   * written three times over for one meaning.
   */
  const groupKey = (fieldId: string, via: string | undefined): string =>
    via === undefined || rows === null
      ? quoteIdent(columnNameFor(fieldId))
      : hopSql(via, fieldId, rows, false);

  /**
   * A multi select on this record holds an array, so its groups are its
   * members: one row per member through `json_each`, and an entry counts in
   * every group it belongs to (Decision 8 of
   * `dependent-choices-and-multi-select.md`). Null for every other group.
   */
  const membersJoin = (fieldId: string, via: string | undefined): string | null => {
    if (via !== undefined || rows === null) return null;
    return isMultiSelect(rows, recordId, fieldId)
      ? `, json_each(${quoteIdent(columnNameFor(fieldId))}) AS ${MEMBER}`
      : null;
  };

  switch (spec.shape) {
    case 'scalar': {
      // The measure is written first in the statement, so whatever it binds is
      // pushed first. A recipe measure is the only measure that binds anything.
      const { fn, inner } = aggregateOf(spec.measure, rows, params);
      if (spec.leaderOf === undefined) {
        return {
          sql: `SELECT ${fn}(${inner}) AS "value" ${from(recordId)} ${where(spec.scope)}`,
          params,
        };
      }
      // The largest group, and which group it was. A row with no group is in
      // no group, so it is excluded rather than ranked as an empty one.
      if (spec.leaderVia !== undefined && rows === null) {
        throw new InsightCompileError('a ranking through a reference needs the schemas');
      }
      const members = membersJoin(spec.leaderOf, spec.leaderVia);
      const key = members === null ? groupKey(spec.leaderOf, spec.leaderVia) : MEMBER_KEY;
      return {
        sql: ranked(
          `SELECT ${key} AS "key", ${fn}(${inner}) AS "value" ${from(recordId)}${members ?? ''} ` +
            `${where(spec.scope)} AND ${key} IS NOT NULL ` +
            `GROUP BY ${key}`,
          members !== null,
          '1',
        ),
        params,
      };
    }

    case 'scalar_pair': {
      // A pair IS one period against the period immediately before it, and
      // `previousWindowFor` derives that from the spec's own scope. An outside
      // window leaves no "before" to compare against, so there is no honest
      // number to draw. The caller hides these cards before it gets here; this
      // throw is the backstop, and `computeInsight`'s catch turns a slip into a
      // quiet empty card rather than a crash.
      if (cut?.window != null) {
        throw new Error('A scalar_pair card cannot take an outside window.');
      }
      // Two windows, one round trip: each aggregate sees only its own days,
      // and the outer WHERE spans both so the scan stays on the day index.
      const current = windowFor(spec.scope, today);
      const previous = previousWindowFor(spec.scope, today);
      // The measure is written once per slice, so it is compiled once per
      // slice: one string reused would put two `?` marks in the text for one
      // pushed value.
      const slice = (window: DayWindow): string => {
        params.push(window.from, window.to);
        const { fn, inner } = aggregateOf(spec.measure, rows, params);
        return `${fn}(CASE WHEN day >= ? AND day <= ? THEN ${inner} END)`;
      };
      const value = slice(current);
      const against = slice(previous);
      params.push(previous.from, current.to);
      // The conditions sit on the outer WHERE, so both windows honour them.
      const both = conditions();
      return {
        sql:
          `SELECT ${value} AS "value", ${against} AS "against" ${from(recordId)} ` +
          `WHERE deleted_at IS NULL AND day >= ? AND day <= ?${both}`,
        params,
      };
    }

    case 'series': {
      // A series carries `days`, not a scope, so a cut's window replaces the
      // span rather than the period. The caller fills the gaps over the same
      // window, or the line would be drawn against the wrong days.
      const window = cut?.window ?? seriesWindow(spec.days, today);
      const { fn, inner } = aggregateOf(spec.measure, rows, params);
      params.push(window.from, window.to);
      const both = conditions();
      return {
        sql:
          `SELECT day AS "day", ${fn}(${inner}) AS "value" ${from(recordId)} ` +
          `WHERE deleted_at IS NULL AND day >= ? AND day <= ?${both} ` +
          `GROUP BY day ORDER BY day ASC`,
        params,
      };
    }

    case 'breakdown': {
      if (spec.groupVia !== undefined && rows === null) {
        throw new InsightCompileError('a group through a reference needs the schemas');
      }
      const members = membersJoin(spec.groupFieldId, spec.groupVia);
      const key = members === null ? groupKey(spec.groupFieldId, spec.groupVia) : MEMBER_KEY;
      const { fn, inner } = aggregateOf(spec.measure, rows, params);
      const clause = where(spec.scope);
      params.push(GROUP_SCAN_LIMIT);
      return {
        sql: ranked(
          `SELECT ${key} AS "key", ${fn}(${inner}) AS "value" ${from(recordId)}${members ?? ''} ` +
            `${clause} AND ${key} IS NOT NULL ` +
            `GROUP BY ${key}`,
          members !== null,
          '?',
        ),
        params,
      };
    }
  }
};

/* ---------------------------------------------------------- combined cards */

/**
 * A combined card: one subquery per total, arithmetic over them.
 *
 * "What each customer owes" is the deliveries minus the payments, and no row in
 * either record can be asked it. So the statement is not a GROUP BY over one
 * view — it is a scalar subquery per leaf, each over its OWN record's view with
 * its own conditions, and the four operators above them.
 *
 * The window is the same for every leaf, because a card names one period. What
 * differs per leaf is the record, the total and the conditions.
 *
 * A SUM of no rows is written as zero. SQLite answers NULL there, which is the
 * right answer to "what is the average of nothing" and the wrong one to "what
 * does this customer owe" — a customer who has paid nothing owes the whole
 * delivery, not nothing at all. An average, a smallest and a largest keep their
 * NULL, because for those there really is no number.
 */
const leafSql = (
  leaf: InsightAggLeaf,
  host: string,
  ctx: InsightCompileContext,
  params: (string | number)[],
  window: DayWindow | null,
  /**
   * What only the host's leaves take: the card's own conditions and the
   * session filter. Both name columns of the record whose list is on screen,
   * so a leaf in another record is not what they were about.
   */
  hostOnly: {
    readonly filters?: readonly InsightFilter[];
    readonly predicate?: RuntimeCut['predicate'];
  },
  correlate: (rows: RowExprContext) => string,
): string => {
  const record = leaf.recordId ?? host;
  const rows: RowExprContext = { schemaOf: ctx.schemaOf, recordId: record };

  const fn = aggFn(leaf.agg);
  const inner = innerOf(leaf.agg, leaf.of, rows, params);
  const total = leaf.agg === 'sum' ? `IFNULL(${fn}(${inner}), 0)` : `${fn}(${inner})`;

  const parts: string[] = [];
  if (window !== null) {
    params.push(window.from, window.to);
    parts.push('day >= ?', 'day <= ?');
  }
  const conditions =
    record === host ? [...(hostOnly.filters ?? []), ...(leaf.filters ?? [])] : (leaf.filters ?? []);
  for (const filter of conditions) {
    const predicate = filterSql(filter, params, rows);
    if (predicate !== null) parts.push(predicate);
  }
  if (record === host && hostOnly.predicate != null) {
    params.push(...hostOnly.predicate.params);
    parts.push(`(${hostOnly.predicate.sql})`);
  }
  const tail = correlate(rows);
  return (
    `(SELECT ${total} ${from(record)} WHERE deleted_at IS NULL` +
    `${parts.map((one) => ` AND ${one}`).join('')}${tail})`
  );
};

/** Every total in one tree, so the cap can be counted before anything is built. */
const leavesOf = (node: InsightAggNode): readonly InsightAggLeaf[] => {
  if (node.kind === 'agg') return [node];
  if (node.kind === 'calc') return [...leavesOf(node.left), ...leavesOf(node.right)];
  if (node.kind === 'round') return leavesOf(node.of);
  return [];
};

/** The aggregate tree as one expression. `const`, `calc` and `round` as in a row. */
const treeSql = (
  node: InsightAggNode,
  build: (leaf: InsightAggLeaf) => string,
  params: (string | number)[],
): string => {
  const part = (kid: InsightAggNode): string => treeSql(kid, build, params);

  if (node.kind === 'agg') return build(node);
  if (node.kind === 'const') {
    params.push(node.value);
    return '?';
  }
  if (node.kind === 'round') return `ROUND(${part(node.of)}, ${Math.trunc(node.decimals)})`;
  if (node.op === 'divide') {
    // Named three times in the text, so compiled three times — the same rule
    // `compileRowExpr` keeps, and for the same reason.
    const zero = part(node.right);
    const missing = part(node.right);
    const left = part(node.left);
    const right = part(node.right);
    return `CASE WHEN (${zero}) = 0 OR (${missing}) IS NULL THEN NULL ELSE (${left}) / (${right}) END`;
  }
  const left = part(node.left);
  const right = part(node.right);
  return `(${left} ${CALC_SIGNS[node.op]} ${right})`;
};

/** The days a combined series covers: every day any of its records wrote on. */
const dayKeysCte = (
  records: readonly string[],
  params: (string | number)[],
  window: DayWindow,
): string => {
  const halves = records.map((record) => {
    params.push(window.from, window.to);
    return `SELECT DISTINCT day ${from(record)} WHERE deleted_at IS NULL AND day >= ? AND day <= ?`;
  });
  return `WITH days AS (${halves.join(' UNION ')})`;
};

const combinedInsight = (
  spec: InsightSpec,
  expr: InsightAggNode,
  recordId: string,
  today: string,
  cut: RuntimeCut | undefined,
  rows: RowExprContext | null,
): CompiledInsight => {
  if (rows === null) throw new InsightCompileError('a combined card needs the schemas');
  const ctx: InsightCompileContext = { schemaOf: rows.schemaOf };
  const leaves = leavesOf(expr);
  if (leaves.length === 0) throw new InsightCompileError('a combined card with no totals');
  // The cap is a frame budget: each leaf is a subquery the phone runs while the
  // card is on screen.
  if (leaves.length > INSIGHT_AGG_MAX_LEAVES) {
    throw new InsightCompileError('a combined card with too many totals');
  }

  const params: (string | number)[] = [];
  const recordsOf = (): readonly string[] => [
    ...new Set(leaves.map((leaf) => leaf.recordId ?? recordId)),
  ];
  const windowOf = (scope: InsightScope): DayWindow | null =>
    cut?.window ?? (scope === 'all' ? null : windowFor(scope, today));

  const hostOnly = { filters: spec.filters, predicate: cut?.predicate ?? null };
  const noCorrelation = (): string => '';

  switch (spec.shape) {
    case 'scalar': {
      // A combined card answers one number, so it ranks nothing: `leaderOf` has
      // no meaning over totals that are already whole.
      const window = windowOf(spec.scope);
      const value = treeSql(
        expr,
        (leaf) => leafSql(leaf, recordId, ctx, params, window, hostOnly, noCorrelation),
        params,
      );
      return { sql: `SELECT ${value} AS "value"`, params };
    }

    case 'scalar_pair': {
      if (cut?.window != null) {
        throw new Error('A scalar_pair card cannot take an outside window.');
      }
      const current = windowFor(spec.scope, today);
      const previous = previousWindowFor(spec.scope, today);
      const half = (window: DayWindow): string =>
        treeSql(
          expr,
          (leaf) => leafSql(leaf, recordId, ctx, params, window, hostOnly, noCorrelation),
          params,
        );
      // Each leaf appears twice, once per window, and is compiled twice.
      const value = half(current);
      const against = half(previous);
      return { sql: `SELECT ${value} AS "value", ${against} AS "against"`, params };
    }

    case 'series': {
      const window = cut?.window ?? seriesWindow(spec.days, today);
      const cte = dayKeysCte(recordsOf(), params, window);
      const value = treeSql(
        expr,
        (leaf) =>
          leafSql(leaf, recordId, ctx, params, window, hostOnly, () => ' AND day = d.day'),
        params,
      );
      return {
        sql: `${cte} SELECT d.day AS "day", ${value} AS "value" FROM days d ORDER BY d.day ASC`,
        params,
      };
    }

    case 'breakdown': {
      const window = windowOf(spec.scope);
      /**
       * What one leaf's keys line up on.
       *
       * A combined breakdown is only honest when every leaf groups by a
       * reference into the SAME list, so the keys are the same ids. The leaf
       * says which of its own fields that is; the leaf on the host record may
       * take the card's own group instead of repeating it. A leaf that names
       * none cannot line up with anything, and the card draws nothing.
       */
      const groupOf = (leaf: InsightAggLeaf, rowsOf: RowExprContext): string => {
        const field = leaf.groupFieldId ?? (spec.groupVia === undefined ? spec.groupFieldId : null);
        if (field !== null && isMultiSelect(ctx, rowsOf.recordId, field)) {
          // Its column is the whole array; only a plain breakdown reads its members.
          throw new InsightCompileError('a combined breakdown cannot group by a multi select');
        }
        if (leaf.groupFieldId !== undefined) {
          return quoteIdent(columnNameFor(leaf.groupFieldId));
        }
        if ((leaf.recordId ?? recordId) !== recordId) {
          throw new InsightCompileError('a combined breakdown leaf with nothing to group by');
        }
        return spec.groupVia === undefined
          ? quoteIdent(columnNameFor(spec.groupFieldId))
          : hopSql(spec.groupVia, spec.groupFieldId, rowsOf, false);
      };

      // The keys, from every leaf, with the ungrouped left out: a row in no
      // group is in no group, not in an empty one.
      const halves = leaves.map((leaf) => {
        const record = leaf.recordId ?? recordId;
        const key = groupOf(leaf, { schemaOf: ctx.schemaOf, recordId: record });
        const days: string[] = [];
        if (window !== null) {
          params.push(window.from, window.to);
          days.push(' AND day >= ? AND day <= ?');
        }
        return (
          `SELECT DISTINCT ${key} AS "key" ${from(record)} ` +
          `WHERE deleted_at IS NULL${days.join('')} AND ${key} IS NOT NULL`
        );
      });

      const value = treeSql(
        expr,
        (leaf) =>
          leafSql(leaf, recordId, ctx, params, window, hostOnly, (rowsOf) => {
            const key = groupOf(leaf, rowsOf);
            return ` AND ${key} = k."key"`;
          }),
        params,
      );
      params.push(GROUP_SCAN_LIMIT);
      return {
        sql:
          `WITH keys AS (${halves.join(' UNION ')}) ` +
          `SELECT k."key" AS "key", ${value} AS "value" FROM keys k ` +
          `ORDER BY ${RANK} LIMIT ?`,
        params,
      };
    }
  }
};

/* ------------------------------------------------------------- row shaping */

/** One row of a scalar statement. */
export type ScalarRow = { value: number | null; key?: string | number | null };
/** One row of a pair statement. */
export type PairRow = { value: number | null; against: number | null };
/** One row of a series or breakdown statement. */
export type GroupedRow = { key?: string | number | null; day?: string | null; value: number | null };

/** A stored key is a uuid or an option id. SQLite may hand back a number. */
const asKey = (raw: string | number | null | undefined): string | null =>
  raw === null || raw === undefined ? null : String(raw);

const asNumber = (raw: number | null | undefined): number =>
  raw === null || raw === undefined || !Number.isFinite(raw) ? 0 : raw;

/**
 * Fill the days the statement did not answer.
 *
 * `GROUP BY day` skips a day with no entries, and a line with a gap in it
 * draws a lie. Every missing day is zero, which for a sum is the truth.
 */
export const fillSeries = (
  rows: readonly GroupedRow[],
  days: number,
  today: string,
): SeriesResult => {
  const span = Math.max(1, Math.trunc(days));
  const found = new Map<string, number>();
  for (const row of rows) {
    if (typeof row.day === 'string') found.set(row.day, asNumber(row.value));
  }
  const points: SeriesPoint[] = [];
  for (let back = span - 1; back >= 0; back -= 1) {
    const day = addDays(today, -back);
    points.push({ day, value: found.get(day) ?? 0 });
  }
  return { shape: 'series', points };
};

/**
 * The most days one filtered line will draw.
 *
 * A series under a filter takes the filter's window, and a person may pick a
 * year. Three months of points is already more than a phone-width line can
 * separate, so a longer window draws its last 92 days rather than a smear.
 */
export const SERIES_FILL_CAP = 92;

/**
 * The same gap filling, over an arbitrary window rather than a span back from
 * today. What a series card uses when a session filter replaced its days.
 */
export const fillSeriesRange = (
  rows: readonly GroupedRow[],
  window: DayWindow,
): SeriesResult => {
  const found = new Map<string, number>();
  for (const row of rows) {
    if (typeof row.day === 'string') found.set(row.day, asNumber(row.value));
  }
  const span = Math.floor((dayToUtc(window.to) - dayToUtc(window.from)) / DAY_MS) + 1;
  if (span < 1) return { shape: 'series', points: [] };
  const drawn = Math.min(span, SERIES_FILL_CAP);
  const points: SeriesPoint[] = [];
  for (let back = drawn - 1; back >= 0; back -= 1) {
    const day = addDays(window.to, -back);
    points.push({ day, value: found.get(day) ?? 0 });
  }
  return { shape: 'series', points };
};

/** The top `limit` slices, and everything behind them netted into one. */
export const cutBreakdown = (rows: readonly GroupedRow[], limit: number): BreakdownResult => {
  const cap = Math.max(1, Math.trunc(limit));
  const slices: BreakdownSlice[] = [];
  let otherCount = 0;
  let otherValue = 0;
  for (const row of rows) {
    const key = asKey(row.key);
    if (key === null) continue;
    if (slices.length < cap) {
      slices.push({ key, value: asNumber(row.value) });
      continue;
    }
    otherCount += 1;
    otherValue += asNumber(row.value);
  }
  return {
    shape: 'breakdown',
    slices,
    other: otherCount === 0 ? null : { count: otherCount, value: otherValue },
  };
};

/** A scalar statement's rows, as a result. No rows means nothing matched. */
export const readScalar = (rows: readonly ScalarRow[]): ScalarResult => {
  const row = rows[0];
  return {
    shape: 'scalar',
    value: row === undefined || row.value === null ? null : row.value,
    key: asKey(row?.key),
  };
};

/** A pair statement's row, as a result. */
export const readPair = (rows: readonly PairRow[]): PairResult => {
  const row = rows[0];
  return {
    shape: 'scalar_pair',
    value: row?.value ?? null,
    against: row?.against ?? null,
  };
};
